// libs
import { motion } from "framer-motion";
import { useToggle } from "react-use";
import styled from "styled-components";
// components
import { ImageFill } from "../ImageFill";
import { Text } from "../Text";
import Tooltip from "../Tooltip";
// other
import { cursorIcon } from "../../helpers/transformers/interactive-slide";
import { Artifact } from "../../models/client";

interface ATHeroImageProps {
  artifact: Artifact;
  onClick: () => any;
}

const ImgContainer = styled(motion.div)`
  padding-top: 80%;
  position: relative;
  box-shadow: 50px 20px 100px 0 rgba(0, 0, 0, 0.41);
  background-color: white;
  cursor: pointer;
`;

const LabelWrapper = styled.div`
  position: absolute;
  top: 24px;
  left: 24px;
  transform: rotate(-90deg) translateX(-100%);
  transform-origin: 0 0;
`;

const TooltipPosition = styled(motion.div)`
  position: absolute;
  bottom: 32px;
  left: 32px;
  z-index: 1;
`;

const imageVariants = {
  initial: { opacity: 0, x: -20 },
  animate: { opacity: 1, x: 0 },
};

function ATHeroImage({ artifact, onClick }: ATHeroImageProps) {
  const [hovered, setHovered] = useToggle(false);

  const handleMouseOver = () => {
    if (!hovered) {
      setHovered(true);
    }
  };
  return (
    <motion.div>
      <ImgContainer
        variants={imageVariants}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.6, delay: 1, ease: "easeInOut" }}
        onClick={onClick}
        onMouseOver={handleMouseOver}
        onMouseLeave={() => setHovered(false)}
        whileHover={{ scale: 1.01 }}
      >
        <ImageFill
          src={artifact.previewImg.src}
          srcLow={`${artifact.previewImg.src}?width=600`}
          alt={artifact.previewImg.title}
          fit={artifact.previewImg.fit}
        />
        <LabelWrapper>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.6,
              delay: 1.5,
              ease: "easeOut",
            }}
          >
            <Text variant="small">
              {artifact.longName ? artifact.longName : artifact.name}
            </Text>
          </motion.div>
        </LabelWrapper>
        <TooltipPosition>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 1.4,
              ease: "easeOut",
            }}
          >
            <Tooltip icon={cursorIcon(artifact.type)} expanded={hovered}>
              {artifact.helperText}
            </Tooltip>
          </motion.div>
        </TooltipPosition>
      </ImgContainer>
    </motion.div>
  );
}

export default ATHeroImage;
